import { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Save, Truck, Bookmark } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const EMPTY_PKG = { weight: 5, length: 12, width: 12, height: 6 };

// Live shipping rates (ShipNerd). Renders inside the Delivery block when "Shipping" is chosen.
export default function ShipRatesPanel({ defaultZip = "", selected, onRate }) {
  const [dest, setDest] = useState({ name: "", company: "", address1: "", address2: "", city: "", state: "", zip: defaultZip, country: "CA", phone: "", email: "", residential: false });
  const [pkgs, setPkgs] = useState([{ ...EMPTY_PKG }]);
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rates, setRates] = useState([]);
  const [recips, setRecips] = useState([]);
  const [recipId, setRecipId] = useState("");

  const loadLib = () => api.get("/shipping/packages").then((r) => setLibrary(r.data || [])).catch(() => {});
  const loadRecips = () => api.get("/shipping/recipients").then((r) => setRecips(r.data || [])).catch(() => {});
  useEffect(() => { loadLib(); loadRecips(); }, []);
  useEffect(() => { if (defaultZip) setDest((d) => (d.zip ? d : { ...d, zip: defaultZip })); }, [defaultZip]);

  // Auto-fill city/province from the postal code (CA: 3-char FSA, US: 5-digit).
  const autoRef = useRef({ city: "", state: "" });
  const countryAutoRef = useRef("CA");
  useEffect(() => {
    const raw = (dest.zip || "").replace(/\s+/g, "").toUpperCase();
    // Auto-detect country from the code format: numeric → US, letter-digit-letter → CA.
    let country = (dest.country || "CA").toUpperCase();
    let detected = country;
    if (/^\d{3,}$/.test(raw)) detected = "US";
    else if (/^[A-Z]\d[A-Z]/.test(raw)) detected = "CA";
    if (detected !== country && (country === countryAutoRef.current || !dest.country)) {
      countryAutoRef.current = detected;
      setDest((d) => ({ ...d, country: detected }));
      country = detected;
    }
    const need = country === "CA" ? 3 : 5;
    if (raw.length < need) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/shipping/postal-lookup", { params: { country, code: raw } });
        setDest((d) => {
          const nd = { ...d };
          if (data.city && (!d.city || d.city === autoRef.current.city)) nd.city = data.city;
          if (data.state && (!d.state || d.state === autoRef.current.state)) nd.state = data.state;
          autoRef.current = { city: data.city || autoRef.current.city, state: data.state || autoRef.current.state };
          return nd;
        });
      } catch { /* ignore lookup failures */ }
    }, 500);
    return () => clearTimeout(t);
  }, [dest.zip, dest.country]); // eslint-disable-line react-hooks/exhaustive-deps

  const setD = (k, v) => setDest({ ...dest, [k]: v });
  const setPkg = (i, k, v) => setPkgs(pkgs.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)));
  const addPkg = () => setPkgs([...pkgs, { ...EMPTY_PKG }]);
  const rmPkg = (i) => setPkgs(pkgs.length > 1 ? pkgs.filter((_, idx) => idx !== i) : pkgs);
  const addFromLibrary = (id) => {
    const p = library.find((x) => x.id === id);
    if (p) setPkgs([...pkgs, { weight: p.weight, length: p.length, width: p.width, height: p.height }]);
  };
  const savePreset = async (p) => {
    const name = window.prompt("Save this package to your library as:");
    if (!name || !name.trim()) return;
    try {
      await api.post("/shipping/packages", { name: name.trim(), weight: +p.weight || 0, length: +p.length || 0, width: +p.width || 0, height: +p.height || 0 });
      toast.success("Saved to package library"); loadLib();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || e.message); }
  };

  const pkgPayload = () => pkgs.map((p) => ({ weight: +p.weight || 0, length: +p.length || 0, width: +p.width || 0, height: +p.height || 0 }));

  const applyRecip = (id) => {
    const r = recips.find((x) => x.id === id);
    if (!r) return;
    setRecipId(id);
    autoRef.current = { city: r.city || "", state: r.state || "" };
    countryAutoRef.current = r.country || "CA";
    setDest({ name: r.name || "", company: r.company || "", address1: r.address1 || "", address2: r.address2 || "", city: r.city || "", state: r.state || "", zip: r.zip || "", country: r.country || "CA", phone: r.phone || "", email: r.email || "", residential: !!r.residential });
    if ((r.packages || []).length) setPkgs(r.packages.map((p) => ({ weight: p.weight, length: p.length, width: p.width, height: p.height })));
  };

  const saveRecip = async () => {
    const label = window.prompt("Save recipient as (name/label):", dest.name || dest.company || "");
    if (!label || !label.trim()) return;
    try {
      const { data } = await api.post("/shipping/recipients", { label: label.trim(), ...dest, packages: pkgPayload() });
      toast.success("Recipient saved"); setRecipId(data.id); loadRecips();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || e.message); }
  };

  const getRates = async () => {
    setLoading(true); setRates([]); onRate(null);
    try {
      const { data } = await api.post("/shipping/rates", {
        to: { ...dest, zip: dest.zip },
        packages: pkgs.map((p) => ({ weight: +p.weight || 0, length: +p.length || null, width: +p.width || null, height: +p.height || null })),
        residential: !!dest.residential,
      });
      setRates(data.rates || []);
      if (!(data.rates || []).length) toast.info("ShipNerd returned no rates for this shipment");
      // Remember this shipment (address + last box used) on the selected recipient.
      if (recipId) {
        const rr = recips.find((x) => x.id === recipId);
        if (rr) api.post("/shipping/recipients", { label: rr.label, ...dest, packages: pkgPayload() }).then(loadRecips).catch(() => {});
      }
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  const isSel = (r) => selected && selected.service_code === r.service_code && selected.carrier === r.carrier;

  return (
    <div className="mt-2 space-y-2.5 rounded-lg border border-slate-200 bg-slate-50/70 p-2.5" data-testid="ship-rates-panel">
      {/* Destination */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] font-mono uppercase tracking-wide text-slate-400">Ship to</span>
          <div className="flex items-center gap-1.5">
            {recips.length > 0 && (
              <Select value={recipId || undefined} onValueChange={applyRecip}>
                <SelectTrigger data-testid="ship-recip-select" className="h-7 w-44 text-[11px] rounded-lg"><SelectValue placeholder="Saved recipients" /></SelectTrigger>
                <SelectContent>{recips.map((r) => <SelectItem key={r.id} value={r.id}>{r.label}{r.city ? ` · ${r.city}` : ""}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <button type="button" onClick={saveRecip} className="p-1 text-slate-400 hover:text-[#2495D3]" title="Save recipient to address book" data-testid="ship-recip-save"><Bookmark size={14} /></button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          <Input value={dest.name} onChange={(e) => setD("name", e.target.value)} placeholder="Recipient name" data-testid="ship-to-name" className="h-8 text-xs rounded-lg col-span-2" />
          <Input value={dest.address1} onChange={(e) => setD("address1", e.target.value)} placeholder="Street address" data-testid="ship-to-address1" className="h-8 text-xs rounded-lg col-span-2" />
          <Input value={dest.city} onChange={(e) => setD("city", e.target.value)} placeholder="City" data-testid="ship-to-city" className="h-8 text-xs rounded-lg" />
          <Input value={dest.state} onChange={(e) => setD("state", e.target.value.toUpperCase())} placeholder="Prov (BC)" data-testid="ship-to-state" className="h-8 text-xs rounded-lg uppercase" />
          <Input value={dest.zip} onChange={(e) => setD("zip", e.target.value.toUpperCase())} placeholder="Postal code" data-testid="ship-to-zip" className="h-8 text-xs rounded-lg uppercase" />
          <Input value={dest.country} onChange={(e) => setD("country", e.target.value.toUpperCase())} placeholder="Country (CA)" data-testid="ship-to-country" className="h-8 text-xs rounded-lg uppercase" />
          <Input value={dest.phone} onChange={(e) => setD("phone", e.target.value)} placeholder="Phone" data-testid="ship-to-phone" className="h-8 text-xs rounded-lg col-span-2" />
          <Input type="email" value={dest.email} onChange={(e) => setD("email", e.target.value)} placeholder="Email (for shipping notifications)" data-testid="ship-to-email" className="h-8 text-xs rounded-lg col-span-2" />
        </div>
        <label className="flex items-center gap-2 text-[11px] text-slate-600 cursor-pointer">
          <input type="checkbox" checked={dest.residential} onChange={(e) => setD("residential", e.target.checked)} data-testid="ship-to-residential" />
          Residential address
        </label>
      </div>

      {/* Packages */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-mono uppercase tracking-wide text-slate-400">Packages (lbs · in)</span>
          {library.length > 0 && (
            <Select onValueChange={addFromLibrary}>
              <SelectTrigger data-testid="ship-lib-select" className="h-7 w-40 text-[11px] rounded-lg"><SelectValue placeholder="+ From library" /></SelectTrigger>
              <SelectContent>{library.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.weight}lb</SelectItem>)}</SelectContent>
            </Select>
          )}
        </div>
        {pkgs.map((p, i) => (
          <div key={i} className="flex items-center gap-1.5" data-testid={`ship-pkg-row-${i}`}>
            <Input type="number" min={2} value={p.weight} onChange={(e) => setPkg(i, "weight", e.target.value)} placeholder="Wt" data-testid={`ship-pkg-weight-${i}`} className="h-8 w-16 text-xs rounded-lg num text-right" title="Weight (lbs)" />
            <span className="text-slate-300 text-xs">×</span>
            <Input type="number" value={p.length} onChange={(e) => setPkg(i, "length", e.target.value)} placeholder="L" data-testid={`ship-pkg-length-${i}`} className="h-8 w-14 text-xs rounded-lg num text-right" title="Length (in)" />
            <Input type="number" value={p.width} onChange={(e) => setPkg(i, "width", e.target.value)} placeholder="W" data-testid={`ship-pkg-width-${i}`} className="h-8 w-14 text-xs rounded-lg num text-right" title="Width (in)" />
            <Input type="number" value={p.height} onChange={(e) => setPkg(i, "height", e.target.value)} placeholder="H" data-testid={`ship-pkg-height-${i}`} className="h-8 w-14 text-xs rounded-lg num text-right" title="Height (in)" />
            <button type="button" onClick={() => savePreset(p)} className="p-1 text-slate-400 hover:text-[#2495D3]" title="Save to library" data-testid={`ship-pkg-save-${i}`}><Save size={14} /></button>
            <button type="button" onClick={() => rmPkg(i)} className="p-1 text-slate-400 hover:text-red-500" title="Remove" data-testid={`ship-pkg-remove-${i}`}><Trash2 size={14} /></button>
          </div>
        ))}
        <button type="button" onClick={addPkg} className="text-[11px] text-[#2495D3] hover:underline flex items-center gap-1" data-testid="ship-pkg-add"><Plus size={12} />Add package</button>
      </div>

      <Button type="button" onClick={getRates} disabled={loading} data-testid="ship-get-rates" className="w-full h-8 text-xs rounded-lg bg-[#2495D3] hover:bg-[#1E7AA9]">
        {loading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Truck size={14} className="mr-1.5" />}
        {loading ? "Getting rates…" : "Get live rates"}
      </Button>

      {rates.length > 0 && (
        <div className="space-y-1" data-testid="ship-rates-list">
          {rates.map((r, i) => (
            <label key={i} data-testid={`ship-rate-${i}`}
              className={`flex items-center gap-2 rounded-lg border p-2 cursor-pointer text-xs transition-colors ${isSel(r) ? "border-[#2495D3] bg-[#2495D3]/5" : "border-slate-200 hover:bg-white"}`}>
              <input type="radio" name="ship-rate" checked={isSel(r)} onChange={() => onRate(r)} data-testid={`ship-rate-radio-${i}`} />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-700 truncate">{r.carrier} · {r.service_name}</div>
                {r.delivery_time && <div className="text-[10px] text-slate-400">Est. {r.delivery_time}</div>}
              </div>
              <div className="font-semibold text-[#2495D3] num whitespace-nowrap">${r.total.toFixed(2)} {r.currency}</div>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
